import {
  theDomWatcher,
  handleNewsletterSubscribed,
  trackNewsletterSignup,
  initProductDescriptions,
  initVisibleAnchorLinks,
} from "@theme/helpers";

const featureLoaders = [
  {
    label: "cart",
    selectors: ["[data-cart-drawer]", "[data-cart-container]", "[data-open-cart]"],
    observe: true,
    loader: () => import("@theme/cart"),
  },
  {
    label: "wishlist",
    selectors: ["[wishlist-button]", "[wishlist-grid]", "[side-cart-wishlist-grid]"],
    observe: true,
    loader: () => import("@theme/wishlist"),
  },
  {
    label: "side-cart-tabs",
    selectors: ['.side-cart-tabs[role="tablist"]', "[data-cart-tab]"],
    observe: true,
    loader: () => import("@theme/tabs"),
  },
  {
    label: "toasty",
    selectors: ["body"],
    loader: () => import("@theme/toasty"),
  },
  {
    label: "referral-code",
    selectors: ["body"],
    loader: () =>
      import("@theme/referral-code").then(
        ({ initReferralCodeAttribute, initReferralShareSections }) =>
          Promise.all([initReferralCodeAttribute(), initReferralShareSections()]),
      ),
  },
  {
    label: "product-quick-add",
    selectors: ["[quick-add-qf]", "product-form"],
    observe: true,
    loader: () =>
      import("@theme/product-quick-add").then(({ ProductQuickAdd }) => new ProductQuickAdd()),
  },
  {
    label: "quickview",
    selectors: ["product-quick-view", "quickview-modal"],
    loader: () => import("@theme/quickview").then(({ ProductQuickView }) => new ProductQuickView()),
  },
  {
    label: "product-form",
    selectors: ["product-form"],
    observe: true,
    loader: () => import("@theme/option-selectors").then(({ ProductForm }) => new ProductForm()),
  },
  {
    label: "coffee-range-selector",
    selectors: ["[coffee-range-selector]"],
    observe: true,
    loader: () =>
      import("@theme/coffee-range-selector").then(({ initCoffeeRangeSelectors }) =>
        initCoffeeRangeSelectors(),
      ),
  },
  {
    label: "product-card",
    selectors: ["product-card"],
    observe: true,
    loader: () => import("@theme/product-card").then(({ ProductCard }) => new ProductCard()),
  },
  {
    label: "bundle-builder",
    selectors: ["bundle-builder"],
    observe: true,
    loader: () => import("@theme/bundle-builder"),
  },
  {
    // Bundle Builder Free Gift Layer — the engine reacts to cart mutations sitewide (e.g. a
    // subscription removed via the side cart on an unrelated page), so it can't be gated behind
    // an element-observer the way the widget below is. "body" always matches, matching the
    // "toasty" entry's existing convention for always-on loaders.
    label: "bundle-gift-engine",
    selectors: ["body"],
    loader: () => import("@theme/bundle-gift-engine"),
  },
  {
    label: "bundle-gift-widget",
    selectors: ["bundle-gift-widget", "[data-bundle-gift-widget]"],
    observe: true,
    loader: () => import("@theme/bundle-gift-widget"),
  },
  {
    label: "predictive-search",
    selectors: ["predictive-search"],
    loader: () => import("@theme/search").then(({ SearchController }) => new SearchController()),
  },
  {
    label: "menu",
    selectors: ["#open-mobile-menu", ".mobile-menu-outer-wrapper"],
    loader: () => import("@theme/menu").then(({ MenuController }) => new MenuController()),
  },
  {
    label: "accordion",
    selectors: [".accordion"],
    loader: () =>
      import("@theme/accordion").then(({ AccordionController }) => new AccordionController()),
  },
  {
    label: "strength-tabs",
    selectors: ["[data-strength-selector]", "[data-strength-tab]"],
    observe: true,
    loader: () => import("@theme/strength-tabs").then(({ StrengthTabs }) => new StrengthTabs()),
  },
  {
    label: "product-quantity",
    selectors: ["[quantity-select]"],
    observe: true,
    loader: () =>
      import("@theme/product-quantity").then(({ ProductQuantity }) => new ProductQuantity()),
  },
  {
    label: "recharge-submit-price-observer",
    selectors: ["product-form recharge-subscription-widget", "[data-variant-price-submit]"],
    observe: true,
    loader: () =>
      import("@theme/recharge-submit-price-observer").then(
        ({ RechargeSubmitPriceObserver }) => new RechargeSubmitPriceObserver(),
      ),
  },
  {
    label: "recharge-widget-styler",
    selectors: ["recharge-subscription-widget"],
    observe: true,
    loader: () =>
      import("@theme/recharge-widget-styler").then(
        ({ RechargeWidgetStyler }) => new RechargeWidgetStyler(),
      ),
  },
  {
    label: "sticky-add-to-cart",
    selectors: ["[data-sticky-add-to-cart]"],
    observe: true,
    loader: () =>
      import("@theme/sticky-add-to-cart").then(({ StickyAddToCart }) => new StickyAddToCart()),
  },
  {
    label: "countdown",
    selectors: ["countdown-timer"],
    loader: () => import("@theme/countdown"),
  },
  {
    label: "product-delivery-date",
    selectors: ["[data-product-delivery-date]"],
    observe: true,
    loader: () =>
      import("@theme/product-delivery-date").then(
        ({ ProductDeliveryDate }) => new ProductDeliveryDate(),
      ),
  },
  {
    label: "header-scroll",
    selectors: [".header-outer-shopify-wrapper"],
    loader: () =>
      import("@theme/header-scroll").then(
        ({ HeaderScrollController }) => new HeaderScrollController(),
      ),
  },
  {
    label: "modal-controller",
    selectors: ["[data-modal-trigger]", "[data-modal]"],
    loader: () =>
      import("@theme/modal-controller").then(({ ModalController }) => new ModalController()),
  },
  {
    label: "store-locator",
    selectors: ["[data-store-locator]"],
    loader: () => import("@theme/store-locator").then(({ initStoreLocator }) => initStoreLocator()),
  },
];

featureLoaders.forEach(({ selectors, loader, label, observe }) =>
  theDomWatcher(selectors, loader, { label, observe }),
);

theDomWatcher(["swiper-container", ".swiper"], () => import("@theme/swiper"), {
  label: "swiper",
  observe: true,
});

theDomWatcher(
  "swiper-container[navigation]",
  () =>
    import("@theme/swiper-accessibility").then(({ initSwiperAccessibility }) =>
      initSwiperAccessibility(),
    ),
  { label: "swiper-accessibility", observe: true },
);

theDomWatcher("form.newsletter-form", handleNewsletterSubscribed, { label: "newsletter-form" });
theDomWatcher('input[name="contact[email]"]', trackNewsletterSignup, {
  label: "newsletter-signup",
});
theDomWatcher("[read-more-container]", initProductDescriptions, { label: "read-more" });
theDomWatcher("[data-scroll-to-visible-id]", initVisibleAnchorLinks, {
  label: "visible-anchor-link",
});
